import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, Box } from '@mui/material'
import { Warning as WarningIcon, Security as SecurityIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface MfaSetupRequiredModalProps {
  open: boolean
}

export function MfaSetupRequiredModal({ open }: MfaSetupRequiredModalProps) {
  const navigate = useNavigate()

  const handleSetupMfa = () => {
    try {
      navigate('/admin/settings?tab=security')
    } catch (error) {
      console.error('Navigation error:', error)
      // Fallback to direct navigation
      window.location.href = '/admin/settings?tab=security'
    }
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="mfa-setup-required-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="mfa-setup-required-dialog" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" fontSize="large" />
        Multi-Factor Authentication Required
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You must enable Multi-Factor Authentication (MFA) before logging out.
        </Alert>

        <Typography variant="body1" paragraph>
          As an <strong>Administrator</strong>, you are required to set up Multi-Factor Authentication (MFA) for enhanced security.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Important:</strong> If you log out before enabling MFA, you will be unable to log back in. This security measure protects your administrator account from unauthorized access.
        </Typography>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.main', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SecurityIcon sx={{ color: 'white' }} />
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold' }}>
              What is MFA?
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'white' }}>
            MFA adds an extra layer of security by requiring a time-based code from an authenticator app (like Google Authenticator or Microsoft Authenticator) in addition to your password.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          color="warning"
          size="large"
          fullWidth
          onClick={handleSetupMfa}
          startIcon={<SecurityIcon />}
        >
          Set Up MFA Now
        </Button>
      </DialogActions>
    </Dialog>
  )
}
